import { Link } from "wouter";
import { BookOpen, Code2, Database, ExternalLink, FileText, FolderKanban, FolderOpen, Globe2, LayoutDashboard, PanelsTopLeft, Table2, Workflow, X, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { viewPagePath, viewRefKey, type ViewTab } from "@/api/views";
import { useIsMobile } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";

export interface ViewTabsLeadingTab {
  key: string;
  title: string;
  icon?: LucideIcon;
  onSelect: () => void;
}

export interface ViewTabsProps {
  views: ViewTab[];
  activeViewKey?: string;
  /** Optional UI-local tab rendered before configured views. */
  leadingTab?: ViewTabsLeadingTab;
  /** Open the view in an in-app panel instead of navigating to its page. */
  onSelect?: (view: ViewTab) => void;
  onUnpin?: (view: ViewTab) => void;
  className?: string;
}

/** Compact, keyboard-accessible navigation for configured and conversation views. */
export function ViewTabs({ views, activeViewKey, leadingTab, onSelect, onUnpin, className }: ViewTabsProps) {
  const { t } = useTranslation("chat");
  const isMobile = useIsMobile();
  if (!views.length) return null;

  return (
    <nav className={cn("flex min-w-0 gap-1 overflow-x-auto border-b px-3", className)} aria-label={t("views.title")}>
      {leadingTab && <LeadingViewTab tab={leadingTab} activeViewKey={activeViewKey} />}
      {views.map((view) => {
        const key = viewRefKey(view.ref);
        const active = key === activeViewKey;
        const canUnpin = view.ref.scope === "conversation" && view.can_unpin === true;
        const Icon = view.type === "wiki"
          ? BookOpen
          : view.type === "workflow"
            ? Workflow
          : view.type === "kanban_board"
            ? FolderKanban
          : view.type === "file_browser"
            ? view.icon === "file-text"
              ? FileText
              : FolderOpen
            : view.type === "single_file" || view.type === "docx"
              ? FileText
            : view.type === "code_preview_sidebar"
              ? Code2
            : view.type === "visual_editor"
              ? PanelsTopLeft
              : view.type === "dashboard"
                ? LayoutDashboard
              : view.type === "browser_session"
                ? Globe2
              : view.icon === "database"
                ? Database
                : Table2;
        const classNames = cn(
          "flex shrink-0 items-center gap-1.5 px-2.5 py-2 text-xs font-medium transition-colors",
          active
            ? "text-foreground"
            : "text-muted-foreground hover:text-foreground",
        );
        const opensNewTab = view.type === "visual_editor" && !isMobile;
        const contents = <><Icon className="size-3.5" />{view.title}{opensNewTab && <ExternalLink className="size-3 opacity-70" aria-hidden="true" />}</>;

        const selection = onSelect && view.type !== "visual_editor" ? (
          <button
            type="button"
            className={classNames}
            onClick={() => onSelect(view)}
            aria-current={active ? "page" : undefined}
          >
            {contents}
          </button>
        ) : view.type === "visual_editor" ? (
          // Native anchors honor target="_blank"; wouter's Link intercepts left clicks.
          <a
            href={viewPagePath(view.ref)}
            target={opensNewTab ? "_blank" : undefined}
            rel={opensNewTab ? "noopener" : undefined}
            title={opensNewTab ? t("views.opensInNewTab", { title: view.title }) : undefined}
            aria-label={opensNewTab ? t("views.opensInNewTab", { title: view.title }) : undefined}
            className={classNames}
            aria-current={active ? "page" : undefined}
          >
            {contents}
          </a>
        ) : (
          <Link
            href={viewPagePath(view.ref)}
            className={classNames}
            aria-current={active ? "page" : undefined}
          >
            {contents}
          </Link>
        );

        return (
          <div
            key={key}
            className={cn(
              "group relative flex shrink-0 items-center border-b-2",
              active ? "border-primary" : "border-transparent hover:border-border",
            )}
          >
            {selection}
            {canUnpin && onUnpin && (
              <button
                type="button"
                className={cn(
                  "mr-1 flex size-6 items-center justify-center rounded-sm text-muted-foreground transition-opacity hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  !isMobile && "[@media(hover:hover)]:absolute [@media(hover:hover)]:right-0 [@media(hover:hover)]:top-1/2 [@media(hover:hover)]:mr-0 [@media(hover:hover)]:-translate-y-1/2 [@media(hover:hover)]:bg-background [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:group-focus-within:opacity-100 [@media(hover:hover)]:focus-visible:opacity-100",
                )}
                aria-label={t("views.unpin", { title: view.title })}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onUnpin(view);
                }}
              >
                <X className="size-3" />
              </button>
            )}
          </div>
        );
      })}
    </nav>
  );
}

function LeadingViewTab({
  tab,
  activeViewKey,
}: {
  tab: ViewTabsLeadingTab;
  activeViewKey?: string;
}) {
  const active = tab.key === activeViewKey;
  const Icon = tab.icon;
  return (
    <div
      className={cn(
        "group flex shrink-0 items-center border-b-2",
        active ? "border-primary" : "border-transparent hover:border-border",
      )}
    >
      <button
        type="button"
        className={cn(
          "flex shrink-0 items-center gap-1.5 px-2.5 py-2 text-xs font-medium transition-colors",
          active
            ? "text-foreground"
            : "text-muted-foreground hover:text-foreground",
        )}
        onClick={tab.onSelect}
        aria-current={active ? "page" : undefined}
      >
        {Icon && <Icon className="size-3.5" />}
        {tab.title}
      </button>
    </div>
  );
}
